import { useEffect, useState } from 'react';
import { SurveySettings } from '@g17eco/types/survey';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import { DisableUtrsProps, generateDisabledUtrs } from '@utils/survey/getDisableUtrs';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import { getCurrentUser } from '@selectors/user';
import { generateSurveyQuestionsFlexSearchMap } from '@selectors/blueprint';
import { loadScorecardsByInitiativeId } from '@actions/scorecard';
import { loadDelegationUsers } from '../../../slice/surveyDelegationUsers';
import { useGetCustomTagsQuery } from '@api/metric-groups';
import { useGetBlueprintsQuery } from '@api/blueprints';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetMetricGroupsDropdownQuery } from '@api/initiatives';

const initialFilters = {
  filterByStatus: [],
  filterByDelegationStatus: [],
  filterByGoal: [],
  filterByModules: [],
  filterByTag: [],
  searchText: '',
};

type Props = Pick<DisableUtrsProps, 'surveyGroups'> & { survey?: SurveyActionData };

export const useFilterQuestions = ({ surveyGroups, survey }: Props) => {
  const user = useAppSelector(getCurrentUser);
  const dispatch = useAppDispatch();
  const { data: tags = [] } = useGetCustomTagsQuery(survey?.initiativeId ?? skipToken);
  const { data: blueprint } = useGetBlueprintsQuery(survey?.sourceName ?? skipToken);
  const { data: metricGroups = [] } = useGetMetricGroupsDropdownQuery(survey?.initiativeId ?? skipToken);

  const searchIndex = generateSurveyQuestionsFlexSearchMap(survey);

  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    if (!survey?._id || !survey?.initiativeId) {
      return;
    }

    dispatch(loadScorecardsByInitiativeId(survey.initiativeId));
    dispatch(loadDelegationUsers(survey._id));
  }, [survey?.initiativeId, survey?._id, dispatch]);

  const changeFilter = (key: keyof SurveySettings, setting: any) => {
    setFilters((filters) => {
      return { ...filters, [key]: setting };
    });
  };

  const {
    filterByGoal,
    searchText,
    filterByStatus,
    filterByModules,
    filterByDelegationStatus,
    filterByTag,
  } = filters;

  const disabledUTRs = generateDisabledUtrs({
    surveyGroups,
    blueprint,
    filterByGoal,
    searchText,
    filterByStatus,
    filterByModules,
    filterByDelegationStatus,
    filterByTag,
    searchIndex,
    user,
    tags,
    metricGroups,
  });

  return { filters, changeFilter, disabledUTRs };
};
